/*
 * AdminSendReceiptDialog — Modal do admin para enviar um Termo de
 * Ciência e Aceite (recibo com assinatura OTP) para um documento
 * específico da Central de Comunicação (Código de Ética, Banner, Vídeo).
 *
 * Duas abas:
 *   • "Enviar"       — AudiencePicker (postos + contratos + pessoas)
 *                      + preview de destinatários + botão de envio.
 *   • "Assinaturas"  — lista de recibos (assinados + pendentes) com
 *                      link para baixar o PDF individual.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Send, Users, CheckCircle2, Clock, Download, Loader2, FileText,
} from "lucide-react";
import { AudiencePicker } from "./AdminBannersPanel";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const CHANNEL_LABEL = { sms: "SMS", email: "E-mail", whatsapp: "WhatsApp" };

export default function AdminSendReceiptDialog({
  open, onOpenChange, sourceKind, sourceId, documentTitle,
}) {
  const { token } = useAuth();
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [tab, setTab] = useState("send"); // 'send' | 'signatures'
  const [audience, setAudience] = useState({ postos: [], contratos: [], employees: [] });
  const [sendToAll, setSendToAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [dispatchProgress, setDispatchProgress] = useState({ processed: 0, total: 0 });
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadReceipts = useCallback(async () => {
    if (!sourceId || !sourceKind) return;
    setReceiptsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/document-receipts`, {
        params: { source_kind: sourceKind, source_id: sourceId },
        headers: authHeaders,
        timeout: 30000,
      });
      setReceipts(r.data?.items || []);
    } catch (e) {
      toast.error("Falha ao carregar assinaturas");
    } finally {
      setReceiptsLoading(false);
    }
  }, [sourceKind, sourceId, authHeaders]);

  useEffect(() => {
    if (!open) return;
    setTab("send");
    setAudience({ postos: [], contratos: [], employees: [] });
    setSendToAll(false);
    loadReceipts();
  }, [open, loadReceipts]);

  const totalTargeted = useMemo(
    () => (audience.postos?.length || 0)
        + (audience.contratos?.length || 0)
        + (audience.employees?.length || 0),
    [audience],
  );

  const dispatch = async () => {
    if (!sendToAll && totalTargeted === 0) {
      toast.error("Selecione destinatários ou marque 'Enviar para todos'");
      return;
    }
    setSending(true);
    try {
      // Start async job — returns job_id immediately.
      const start = await axios.post(
        `${API}/admin/document-dispatches`,
        {
          source_kind: sourceKind,
          source_id: sourceId,
          audience: sendToAll ? { all: true } : audience,
        },
        { headers: authHeaders, timeout: 60000 },
      );
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Resposta inválida do servidor");

      // Poll every 1s until 'done' or 'error'.
      const result = await new Promise((resolve, reject) => {
        const t = setInterval(async () => {
          try {
            const r = await axios.get(
              `${API}/admin/document-dispatches/job/${jobId}`,
              { headers: authHeaders, timeout: 15000 },
            );
            const job = r.data || {};
            setDispatchProgress({
              processed: job.processed || 0,
              total: job.total || 0,
            });
            if (job.status === "done") { clearInterval(t); resolve(job.result || {}); }
            else if (job.status === "error") {
              clearInterval(t);
              reject(new Error(job.error || "Erro ao processar dispatch"));
            }
          } catch (err) { clearInterval(t); reject(err); }
        }, 1000);
      });

      const { targets = 0, created = 0, reused = 0, skipped_signed = 0 } = result;
      toast.success(
        `Enviado para ${targets} funcionário(s).`
        + (created ? ` ${created} novo(s).` : "")
        + (reused ? ` ${reused} já pendente(s).` : "")
        + (skipped_signed ? ` ${skipped_signed} já assinado(s) foi(ram) ignorado(s).` : ""),
      );
      setTab("signatures");
      loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Falha ao enviar recibos");
    } finally {
      setSending(false);
      setDispatchProgress({ processed: 0, total: 0 });
    }
  };

  const downloadReceipt = async (rec) => {
    setDownloadingId(rec.id);
    try {
      const r = await axios.get(
        `${API}/admin/document-receipts/${rec.id}/pdf`,
        { headers: authHeaders, responseType: "blob", timeout: 60000 },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `termo_${rec.status === "signed" ? "assinado" : "pendente"}_${(rec.employee_matricula || "").slice(0, 8)}_${rec.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error("Falha ao baixar recibo");
    } finally {
      setDownloadingId(null);
    }
  };

  const signedCount = receipts.filter((r) => r.status === "signed").length;
  const pendingCount = receipts.filter((r) => r.status === "pending").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="admin-send-receipt-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-sky-600" />
            Enviar Termo de Aceite
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 truncate">
            {documentTitle}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 -mx-6 px-6">
          <button
            type="button"
            onClick={() => setTab("send")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === "send"
                ? "border-sky-600 text-sky-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="admin-send-tab"
          >
            <Send className="w-4 h-4 inline mr-1.5" />
            Enviar
          </button>
          <button
            type="button"
            onClick={() => { setTab("signatures"); loadReceipts(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === "signatures"
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="admin-signatures-tab"
          >
            <Users className="w-4 h-4 inline mr-1.5" />
            Assinaturas
            {receipts.length > 0 && (
              <Badge
                variant="outline"
                className="ml-2 bg-slate-50 text-[10px] px-1.5 py-0"
              >
                {signedCount}/{receipts.length}
              </Badge>
            )}
          </button>
        </div>

        {/* Panel: Enviar */}
        {tab === "send" && (
          <div className="space-y-4 py-2" data-testid="admin-send-panel">
            <div className="text-xs text-slate-600 bg-sky-50 border border-sky-200 rounded-lg p-3">
              Cada funcionário selecionado receberá o recibo <strong>pendente</strong>.
              Só poderá abrir/usar o app depois de assinar via SMS ou E-mail.
              Funcionários que <strong>já assinaram esse documento</strong> serão ignorados.
            </div>

            {/* Toggle Enviar para todos */}
            <button
              type="button"
              onClick={() => setSendToAll((v) => !v)}
              data-testid="admin-send-to-all-toggle"
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition ${
                sendToAll
                  ? "bg-fuchsia-50 border-fuchsia-400"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className={`w-11 h-6 rounded-full relative transition ${
                sendToAll ? "bg-fuchsia-500" : "bg-slate-300"
              }`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition ${
                  sendToAll ? "left-5" : "left-0.5"
                }`} />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-semibold text-slate-800">
                  Enviar para <strong className="text-fuchsia-700">TODOS os funcionários ativos</strong>
                </div>
                <div className="text-[11px] text-slate-500">
                  Ignora os filtros abaixo e dispara em lote para toda a empresa.
                </div>
              </div>
            </button>

            <div className={sendToAll ? "opacity-40 pointer-events-none select-none" : ""}>
              <AudiencePicker value={audience} onChange={setAudience} />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="text-sm">
                <div className="font-semibold text-slate-800">
                  {sendToAll
                    ? "TODOS os funcionários ativos serão notificados"
                    : totalTargeted === 0
                      ? "Nenhum destinatário selecionado"
                      : `${totalTargeted} filtro(s) selecionado(s)`}
                </div>
                <div className="text-[11px] text-slate-500">
                  {sendToAll
                    ? "Já assinados serão ignorados automaticamente."
                    : "Postos, contratos e pessoas são combinados (OU lógico)."}
                </div>
              </div>
              <Button
                onClick={dispatch}
                disabled={sending || (!sendToAll && totalTargeted === 0)}
                className={sendToAll
                  ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  : "bg-sky-600 hover:bg-sky-700 text-white"}
                data-testid="admin-send-receipt-submit"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    {dispatchProgress.total > 0
                      ? `${dispatchProgress.processed}/${dispatchProgress.total}`
                      : "Enviando…"}</>
                ) : (
                  <><Send className="w-4 h-4 mr-1.5" />
                    {sendToAll ? "Enviar para todos" : "Enviar recibo"}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Panel: Assinaturas */}
        {tab === "signatures" && (
          <div className="space-y-3 py-2" data-testid="admin-signatures-panel">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-emerald-700 text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4" /> Assinaram
                </div>
                <div className="text-2xl font-bold text-emerald-800 mt-0.5">
                  {signedCount}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-medium">
                  <Clock className="w-4 h-4" /> Pendentes
                </div>
                <div className="text-2xl font-bold text-amber-800 mt-0.5">
                  {pendingCount}
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-700 text-xs font-medium">
                  <Users className="w-4 h-4" /> Total
                </div>
                <div className="text-2xl font-bold text-slate-800 mt-0.5">
                  {receipts.length}
                </div>
              </div>
            </div>

            {receiptsLoading ? (
              <div className="py-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : receipts.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nenhum destinatário registrado ainda.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold">Colaborador</th>
                      <th className="text-left px-2 py-2 font-semibold">Posto</th>
                      <th className="text-left px-2 py-2 font-semibold">Status</th>
                      <th className="text-left px-2 py-2 font-semibold">Assinatura</th>
                      <th className="text-right px-2 py-2 font-semibold w-12"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((rec) => (
                      <tr
                        key={rec.id}
                        className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`admin-receipt-row-${rec.id}`}
                      >
                        <td className="px-2 py-2">
                          <div className="font-medium text-slate-800">
                            {rec.employee_nome || "—"}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono">
                            Mat {rec.employee_matricula || "—"} · {rec.employee_cpf || "—"}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-slate-600">
                          {rec.employee_posto || "—"}
                        </td>
                        <td className="px-2 py-2">
                          {rec.status === "signed" ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                              <CheckCircle2 className="w-3 h-3 mr-0.5" />
                              Assinado
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                              <Clock className="w-3 h-3 mr-0.5" />
                              Pendente
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-600">
                          {rec.status === "signed" ? (
                            <>
                              <div>{fmtDateTime(rec.signed_at)}</div>
                              <div className="text-[10px] font-mono text-slate-400">
                                {CHANNEL_LABEL[rec.channel_used] || "—"} · {rec.signature_id || "—"}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400 italic">Aguardando</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => downloadReceipt(rec)}
                            disabled={downloadingId === rec.id}
                            data-testid={`admin-receipt-download-${rec.id}`}
                            className="h-7 w-7 p-0"
                          >
                            {downloadingId === rec.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Download className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="admin-send-receipt-close">
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
